import React, { useState, useEffect } from "react";
import {
  FiFilter,
  FiCheck,
  FiX,
  FiClock,
  FiUser,
  FiPhone,
  FiCalendar,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
} from "react-icons/fi";
import { getWithdrawApprove } from "../../api/endpoints";
import Swal from "../../utils/swalCompat";

import PageHeader from "../../components/PageHeader";

const WithdrawApproved = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ✅ Fetch Approved Withdrawals
  const fetchApprovedWithdrawals = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: search.trim(),
        startDate,
        endDate,
      };

      const res = await getWithdrawApprove(params);
      console.log("Approved Withdrawals:", res);

      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching approved withdrawals:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to fetch approved withdrawals",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedWithdrawals();
  }, [page, search, startDate, endDate]);

  // ✅ Format Date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ Get Status Badge (Approved)
  const getStatusBadge = (status) => {
    return (
      <span className="badge-light-success">
        <FiCheckCircle /> Approved
      </span>
    );
  };

  // ✅ Payment Method Display
  const getPaymentDisplay = (type, data) => {
    if (type === "UPI") return `UPI: ${data.upiId || "N/A"}`;
    if (type === "BANK") return `Bank: ${data.accountNumber || "N/A"}`;
    return type || "N/A";
  };

  return (
    <div className="lead-page">

      <div className="withdraw-approved-page">
        {/* Header */}
        <PageHeader
          eyebrow="Withdraw Payment"
          title="Withdraw Approved"
          subtitle="View all approved withdrawal requests with payment details."
          icon={<FiCheckCircle />}
          tone="success"
          stat={{ label: "Total Approved", value: total }}
        />

        {/* Card */}
        <div className="card shadow-sm border-0">
          {/* Card Header */}
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap border-bottom">
            <h5 className="mb-0 fw-bold">
              <FiCheckCircle className="me-2 text-success" />
              Approved Withdrawal Requests
            </h5>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {showFilter && (
                <>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by User, Mobile..."
                    value={search}
                    onChange={(e) => {
                      setPage(1);
                      setSearch(e.target.value);
                    }}
                    style={{ width: "200px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => {
                      setPage(1);
                      setStartDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => {
                      setPage(1);
                      setEndDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                </>
              )}
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FiFilter /> Filter
              </button>
              <button
                className="btn btn-success btn-sm"
                onClick={fetchApprovedWithdrawals}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <FiRefreshCw /> Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Card Body */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th style={{ minWidth: "220px" }}>
                      <div className="d-flex flex-column">
                        <span>User Details</span>
                        <small className="fw-normal text-muted">Mobile / Referral</small>
                      </div>
                    </th>
                    <th style={{ width: "120px" }}>Deposit Bal.</th>
                    <th style={{ width: "120px" }}>Winning Bal.</th>
                    <th style={{ width: "130px" }}>Withdraw Amount</th>
                    <th style={{ minWidth: "200px" }}>Payment Details</th>
                    <th style={{ minWidth: "150px" }}>Request Date</th>
                    <th style={{ minWidth: "150px" }}>Approved At</th>
                    <th style={{ width: "110px" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 text-muted">Loading approved withdrawals...</div>
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((item, index) => (
                      <tr key={item._id}>
                        <td className="fw-bold">{(page - 1) * limit + index + 1}</td>

                        {/* User Details */}
                        <td>
                          <div className="d-flex flex-column">
                            <div className="d-flex align-items-center gap-1">
                              <FiUser size={12} className="text-success" />
                              <strong>
                                {item.userName && item.userName.trim() !== ""
                                  ? item.userName
                                  : `User (${item.userMobile})`}
                              </strong>
                            </div>
                            <div className="text-muted small">
                              <FiPhone size={10} /> {item.userMobile || "N/A"}
                            </div>
                            <div className="text-muted small">
                              <span className="badge bg-light text-dark">
                                Ref: {item.referralCode || "-"}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Wallet Balances */}
                        <td>
                          <span className="fw-bold text-success">
                            ₹ {item.depositBalance?.toLocaleString() || 0}
                          </span>
                        </td>
                        <td>
                          <span className="fw-bold text-primary">
                            ₹ {item.winningBalance?.toLocaleString() || 0}
                          </span>
                        </td>

                        {/* Withdraw Amount */}
                        <td>
                          <span className="fw-bold text-danger fs-5">
                            ₹ {item.amount?.toLocaleString() || 0}
                          </span>
                        </td>

                        {/* Payment Details */}
                        <td>
                          <div className="d-flex flex-column small">
                            <span className="fw-bold">{item.withdrawType}</span>
                            <span className="text-muted">
                              {getPaymentDisplay(item.withdrawType, item)}
                            </span>
                            {item.withdrawType === "BANK" && (
                              <span className="text-muted">{item.ifscCode || ""}</span>
                            )}
                          </div>
                        </td>

                        {/* Request Date */}
                        <td>
                          <div className="small">
                            <FiCalendar size={10} className="me-1" />
                            {formatDate(item.createdAt)}
                          </div>
                        </td>

                        {/* Approved At */}
                        <td>
                          <div className="small text-success">
                            <FiCheckCircle size={10} className="me-1" />
                            {formatDate(item.approvedAt)}
                          </div>
                        </td>

                        {/* Status */}
                        <td>{getStatusBadge(item.status)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="text-muted">
                          <FiCheckCircle size={40} className="mb-3 d-block mx-auto text-secondary" />
                          <h5 className="fw-bold">No Approved Withdrawals</h5>
                          <p className="small">No withdrawal requests have been approved yet</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div>
                  <span className="text-muted small">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, total)} of {total} entries
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <FiChevronLeft /> Previous
                  </button>
                  <span className="fw-bold px-3">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawApproved;
